/**
 * Plan limits + pricing — single source of truth.
 *
 * MODEL: Prijs per scherm. Totaalbedrag = aantal schermen × prijs/scherm.
 *
 * Voorbeelden:
 *   - 1 scherm Gratis: €0
 *   - 3 schermen Standaard maandelijks: 3 × €19,95 = €59,85/mnd
 *   - 5 schermen Premium jaarlijks: 5 × €29,95 × 12 = €1.797/jaar
 *
 * Alle prijzen exclusief 21% btw. Mollie verwerkt btw op de invoice.
 */

export type PlanId = 'FREE' | 'LITE' | 'STANDARD' | 'PREMIUM' | 'ENTERPRISE';
export type BillingCycle = 'monthly' | 'yearly';

export type Plan = {
  id: PlanId;
  name: string;
  description: string;
  // Pricing (in euro, ex btw, PER SCREEN)
  pricePerScreenMonth: number | null;        // billed monthly
  pricePerScreenYearly: number | null;       // monthly equivalent when billing yearly
  // Hard limits
  maxScreens: number;       // 0 = unlimited (only Enterprise)
  storageMaxMb: number;
  uploadMaxMb: number;      // single file size limit
  // Feature flags
  watermark: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  apiAccess: boolean;
  // Marketing copy
  features: string[];
  cta: string;
  highlight?: boolean;
};

export const PLANS: Record<PlanId, Plan> = {
  FREE: {
    id: 'FREE',
    name: 'Gratis',
    description: 'Probeer Castly uit met één scherm.',
    pricePerScreenMonth: 0,
    pricePerScreenYearly: 0,
    maxScreens: 1,
    storageMaxMb: 100,
    uploadMaxMb: 25,
    watermark: true,
    customBranding: false,
    prioritySupport: false,
    apiAccess: false,
    features: [
      '1 scherm',
      '100 MB cloud-opslag',
      'Afbeeldingen, video\'s en documenten',
      'Beheer schermen vanaf web of mobiel',
      'Castly logo zichtbaar op scherm',
    ],
    cta: 'Begin gratis',
  },
  LITE: {
    id: 'LITE',
    name: 'Lite',
    description: 'Eén scherm, instaptarief.',
    pricePerScreenMonth: null,         // alleen jaarlijks gefactureerd
    pricePerScreenYearly: 3.99,        // €3,99/maand · jaarlijks gefactureerd = €47,88/jaar
    maxScreens: 1,
    storageMaxMb: 50,
    uploadMaxMb: 2,
    watermark: true,                   // Castly logo zichtbaar (klein, in hoek)
    customBranding: false,
    prioritySupport: false,
    apiAccess: false,
    features: [
      '1 scherm',
      '50 MB cloud-opslag',
      'Afbeeldingen, video\'s en documenten',
      'Beheer schermen vanaf web of mobiel',
      'Klein Castly logo in hoek',
    ],
    cta: 'Start je 7 dagen trial',
  },
  STANDARD: {
    id: 'STANDARD',
    name: 'Standaard',
    description: 'Voor 2 tot 10 schermen, met reviews en korting.',
    pricePerScreenMonth: 19.95,
    pricePerScreenYearly: 14.95,
    maxScreens: 10,
    storageMaxMb: 10_000,
    uploadMaxMb: 250,
    watermark: false,
    customBranding: false,
    prioritySupport: false,
    apiAccess: false,
    features: [
      'Tot 10 schermen',
      '10 GB cloud-opslag',
      'Geen Castly watermerk',
      'Playlists en planning',
      'Google Reviews op scherm',
      'Reviews + QR korting',
      'E-mail support',
    ],
    cta: 'Start je 7 dagen trial',
    highlight: true,
  },
  PREMIUM: {
    id: 'PREMIUM',
    name: 'Pro',
    description: 'Voor groeiende organisaties — wachtrij, huisstijl, tot 50 schermen.',
    pricePerScreenMonth: 36.95,
    pricePerScreenYearly: 29.95,
    maxScreens: 50,
    storageMaxMb: 50_000,
    uploadMaxMb: 1_000,
    watermark: false,
    customBranding: true,
    prioritySupport: true,
    apiAccess: false,
    features: [
      'Tot 50 schermen',
      '50 GB cloud-opslag',
      'Alles uit Standaard',
      'Wachtrij/oproep systeem',
      'Eigen logo en huisstijl',
      'Prioriteit support',
      'Geavanceerde planning',
    ],
    cta: 'Start je 7 dagen trial',
  },
  ENTERPRISE: {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    description: '50+ schermen, op maat.',
    pricePerScreenMonth: null,
    pricePerScreenYearly: null,
    maxScreens: 0, // unlimited
    storageMaxMb: 500_000,
    uploadMaxMb: 2_000,
    watermark: false,
    customBranding: true,
    prioritySupport: true,
    apiAccess: true,
    features: [
      'Onbeperkt aantal schermen',
      'Alles uit Pro',
      '500+ GB opslag',
      'API toegang',
      'SLA en dedicated support',
      'On-premise deployment mogelijk',
      'Custom integraties',
    ],
    cta: 'Neem contact op',
  },
};

export function getPlan(planId: PlanId | string): Plan {
  return PLANS[planId as PlanId] ?? PLANS.FREE;
}

/** Format price as €xx,xx */
export function formatPrice(amount: number): string {
  return `€${amount.toFixed(2).replace('.', ',')}`;
}

/**
 * Returns price per scherm/month for the given billing cycle.
 */
export function getPricePerScreen(plan: Plan, cycle: BillingCycle): number | null {
  // For yearly cycle, always use yearly rate (if set)
  if (cycle === 'yearly') return plan.pricePerScreenYearly;
  // For monthly cycle, only return if monthly rate is set (some plans like LITE are yearly-only)
  return plan.pricePerScreenMonth;
}

/**
 * Total amount to charge for a subscription.
 * Monthly: screens × pricePerScreenMonth
 * Yearly: screens × pricePerScreenYearly × 12 (charged once per year)
 */
export function getTotalPrice(plan: Plan, screens: number, cycle: BillingCycle): number | null {
  const perScreen = getPricePerScreen(plan, cycle);
  if (perScreen === null) return null;
  if (cycle === 'yearly') return screens * perScreen * 12;
  return screens * perScreen;
}

/** Yearly savings vs monthly billing for given screen count. */
export function getYearlySavings(plan: Plan, screens: number): number {
  if (plan.pricePerScreenMonth === null || plan.pricePerScreenYearly === null) return 0;
  return (plan.pricePerScreenMonth - plan.pricePerScreenYearly) * 12 * screens;
}

/**
 * Returns true if the tenant has hit or exceeded their screen limit.
 */
export function isOverScreenLimit(tenant: { plan: PlanId | string }, currentScreenCount: number): boolean {
  const plan = getPlan(tenant.plan);
  if (plan.maxScreens === 0) return false; // unlimited
  return currentScreenCount >= plan.maxScreens;
}

/**
 * Returns true if uploading a file of the given size would exceed storage limit.
 */
export function isOverStorageLimit(
  tenant: { plan: PlanId | string; storageUsedMb: number; storageMaxMb: number },
  fileSizeBytes: number
): { over: boolean; reason?: string } {
  const plan = getPlan(tenant.plan);
  const fileMb = fileSizeBytes / (1024 * 1024);

  if (fileMb > plan.uploadMaxMb) {
    return {
      over: true,
      reason: `Bestand is ${fileMb.toFixed(1)} MB. Het ${plan.name}-plan staat maximaal ${plan.uploadMaxMb} MB per bestand toe.`,
    };
  }

  const limit = plan.storageMaxMb;
  if (tenant.storageUsedMb + fileMb > limit) {
    const remaining = Math.max(0, limit - tenant.storageUsedMb);
    return {
      over: true,
      reason: `Niet genoeg ruimte. Je hebt nog ${remaining.toFixed(0)} MB vrij van je ${limit} MB ${plan.name}-plan.`,
    };
  }

  return { over: false };
}

/**
 * @deprecated Gebruik `hasCustomBranding` uit `@/lib/features` (async, met
 * tenant-overrides). Deze synchrone variant blijft alleen voor backwards-compat
 * met code die alleen het plan-id heeft en geen DB-toegang.
 */
export function hasCustomBranding(planId: PlanId | string): boolean {
  const plan = getPlan(planId);
  return Boolean(plan.customBranding);
}

/**
 * @deprecated Gebruik `hasQueueSystem` uit `@/lib/features` (async, met
 * tenant-overrides). Deze synchrone variant blijft alleen voor backwards-compat.
 */
export function hasQueueSystem(planId: PlanId | string): boolean {
  const plan = getPlan(planId);
  return Boolean(plan.customBranding); // tied to same Premium gate as branding
}

/**
 * @deprecated Gebruik `hasReviews` uit `@/lib/features` (async, met
 * tenant-overrides). Deze synchrone variant blijft alleen voor backwards-compat.
 */
export function hasReviews(planId: PlanId | string): boolean {
  // Standard, Premium (Pro), Enterprise — alles behalve Free en Lite
  return planId === 'STANDARD' || planId === 'PREMIUM' || planId === 'ENTERPRISE';
}
